import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Container,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Notifications,
  Send,
  DoneAll,
  Cancel,
  TrendingUp,
} from '@mui/icons-material';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';

interface NotificationStats {
  total: number;
  successful: number;
  failed: number;
  byType: {
    single: number;
    broadcast: number;
    filtered: number;
    scheduled: number;
  };
  byDay: { [key: string]: number };
}

interface NotificationHistory {
  id: string;
  type: string;
  status: string;
  notification: any;
  targetUserId?: string;
  results?: any;
  sentAt: Date;
  sentBy?: string;
  error?: string;
}

const NotificationAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [history, setHistory] = useState<NotificationHistory[]>([]);
  const [timeRange, setTimeRange] = useState('30d');
  const [trendData, setTrendData] = useState<any[]>([]);
  const [typeDistribution, setTypeDistribution] = useState<any[]>([]);

  const fetchNotificationData = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const [statsResponse, historyResponse] = await Promise.all([
        axios.get('https://api.sobcof.com/api/notifications/stats', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('https://api.sobcof.com/api/notifications/history', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
      ]);

      let fetchedStats = null;
      if (statsResponse.data.success) {
        fetchedStats = statsResponse.data.data;
        setStats(fetchedStats);
      }

      if (historyResponse.data.success) {
        const historyData = Array.isArray(historyResponse.data.data) ? historyResponse.data.data : [];
        setHistory(historyData);

        // Calculate type distribution from stats
        if (fetchedStats?.byType) {
          setTypeDistribution(
            Object.entries(fetchedStats.byType).map(([type, count]) => ({ type, count: count as number }))
          );
        }

        // Calculate trend data from stats.byDay
        if (fetchedStats?.byDay) {
          const trendArray = Object.entries(fetchedStats.byDay)
            .sort(([a], [b]) => a.localeCompare(b))
            .slice(-30)
            .map(([date, count]) => ({
              date: new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
              sent: count as number,
            }));
          setTrendData(trendArray);
        }
      }
    } catch (error) {
      console.error('Error fetching notification data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotificationData();
  }, [fetchNotificationData]);

  const COLORS = ['#8B4513', '#D2691E', '#CD853F', '#DEB887', '#F4A460', '#FF6B6B'];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#8B4513' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2, width: 40, height: 40 }}>
            <Notifications sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'white' }}>
            Bildirim Performans Analizi
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1 }}>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
            >
              <MenuItem value="7d">Son 7 Gün</MenuItem>
              <MenuItem value="30d">Son 30 Gün</MenuItem>
              <MenuItem value="90d">Son 90 Gün</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Key Metrics */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#667eea', mr: 2 }}>
                    <Send />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea' }}>
                      {(stats?.total || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Bildirim
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#4CAF50', mr: 2 }}>
                    <DoneAll />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                      {(stats?.successful || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Başarılı
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#FF9800', mr: 2 }}>
                    <TrendingUp />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF9800' }}>
                      {(stats?.failed || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Başarısız
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#FF6B6B', mr: 2 }}>
                    <Cancel />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF6B6B' }}>
                      {stats?.failed.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Başarısız
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Performance Overview */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '2 1 500px', minWidth: '400px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Bildirim Performans Trendi (Son 30 Gün)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="sent"
                    stroke="#667eea"
                    strokeWidth={2}
                    name="Gönderilen"
                  />
                  <Line
                    type="monotone"
                    dataKey="opened"
                    stroke="#4CAF50"
                    strokeWidth={2}
                    name="Açılan"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Bildirim Türü Dağılımı
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={typeDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ type, count }) => `${type}: ${count}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {typeDistribution.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Box>
        </Box>

        {/* Engagement Rates */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3, mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Etkileşim Hunisi
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#667eea', mb: 1 }}>
                  {(stats?.total || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Toplam Bildirim
                </Typography>
                <Box sx={{ width: '100%', height: 8, bgcolor: '#667eea', borderRadius: 1 }} />
              </Box>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#4CAF50', mb: 1 }}>
                  {(stats?.successful || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Başarılı ({stats && stats.total > 0 ? ((stats.successful / stats.total) * 100).toFixed(1) : 0}%)
                </Typography>
                <Box
                  sx={{
                    width: `${stats && stats.total > 0 ? (stats.successful / stats.total) * 100 : 0}%`,
                    height: 8,
                    bgcolor: '#4CAF50',
                    borderRadius: 1
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#f44336', mb: 1 }}>
                  {(stats?.failed || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Başarısız ({stats && stats.total > 0 ? ((stats.failed / stats.total) * 100).toFixed(1) : 0}%)
                </Typography>
                <Box
                  sx={{
                    width: `${stats && stats.total > 0 ? (stats.failed / stats.total) * 100 : 0}%`,
                    height: 8,
                    bgcolor: '#f44336',
                    borderRadius: 1
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Card>

        {/* Recent Notifications */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Son Gönderilen Bildirimler
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Başlık</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tür</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Hedef</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Başarılı</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Başarısız</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.slice(0, 10).map((item) => {
                  const title = item.notification?.title || 'Bildirim';
                  const message = item.notification?.body || item.notification?.message || '';
                  const resultSuccess = item.results?.successCount || 0;
                  const resultFailure = item.results?.failureCount || 0;

                  return (
                    <TableRow
                      key={item.id}
                      sx={{
                        '&:hover': { backgroundColor: '#f8fafc' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {message ? message.substring(0, 50) + (message.length > 50 ? '...' : '') : 'Mesaj yok'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.type || 'N/A'} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.targetUserId || 'Tüm kullanıcılar'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={resultSuccess.toLocaleString()}
                          size="small"
                          sx={{ bgcolor: '#4CAF50', color: 'white' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={resultFailure.toLocaleString()}
                          size="small"
                          color="error"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.status || 'N/A'}
                          size="small"
                          color={item.status === 'sent' || item.status === 'completed' ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.sentAt ? new Date(item.sentAt).toLocaleDateString('tr-TR') : 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </Box>
  );
};

export default NotificationAnalyticsPage;
